import { ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { formatUnits } from "viem";
import { formatTimestamp } from "../../utils";
import { TransactionTypeChip } from "../bridge/TransactionTypeChip";
import { TxHashLink } from "../TxHashLink";
import { TransferCard } from "../transfers/TransferCard";
import type { TransferTxFailed, TransferTxResult, TransferTxSuccess } from "../transfers/transfersConfig";

export function TransfersTxDetails({ txData }: { txData: TransferTxResult }) {
  if (txData.txStatus === "success") {
    return <TransferTxSuccessDetails txData={txData} />;
  }
  return <TransferTxFailedDetails txData={txData} />;
}

function TransferTxSuccessDetails({ txData }: { txData: TransferTxSuccess }) {
  const displayAmount = formatUnits(txData.amount, txData.tokenDecimals);

  return (
    <ModalContent>
      <ModalHeader>Transfers details</ModalHeader>
      <ModalBody>
        <TransferCard
          tokenSymbol={txData.tokenSymbol}
          displayAmount={Number(displayAmount)}
          senderAddress={txData.l2AccountAddress.toString()}
          recipientAddress={txData.recipientAddress.toString()}
        />
        <div className="w-full space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Transaction type</div>
            <TransactionTypeChip type={txData.transferMode} />
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Date & Time</div>
            <div className="font-semibold">{formatTimestamp(txData.timestamp)}</div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Transaction hash</div>
            <div className="font-semibold">
              <TxHashLink txHash={txData.txHash} source="l2" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">From</div>
            <div className="font-semibold">
              <TxHashLink txHash={txData.l2AccountAddress.toString()} source="l2" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">To</div>
            <div className="font-semibold">
              <TxHashLink txHash={txData.recipientAddress.toString()} source="l2" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Network Fee</div>
            <div className="font-semibold">0.00005 ETH</div>
          </div>
        </div>
      </ModalBody>
    </ModalContent>
  );
}

function TransferTxFailedDetails({ txData }: { txData: TransferTxFailed }) {
  return (
    <ModalContent>
      <ModalHeader>Transfers details</ModalHeader>
      <ModalBody>
        <div className="w-full space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Status</div>
            <div className="font-semibold text-[#FF4D4D]">Failed</div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Transaction type</div>
            <TransactionTypeChip type={txData.transferParams.transferMode} />
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Date & Time</div>
            <div className="font-semibold">{formatTimestamp(txData.timestamp)}</div>
          </div>
        </div>

        <details className="w-full bg-[#3F6784] p-4 rounded-2xl space-y-3 text-sm">
          <summary className="font-semibold text-[#ADC0D0] cursor-pointer">Error message</summary>
          <div className="font-semibold text-[#FF4D4D] mt-2 overflow-auto max-h-48">{txData.errorMessage}</div>
        </details>
      </ModalBody>
    </ModalContent>
  );
}
